import { useEffect, useState } from 'react';
import { getMe, getStats, getReferrals, getLeaderboard } from './api';
import ShareKit from './ShareKit';

const gbp = (minor) => `£${((Number(minor) || 0) / 100).toFixed(2)}`;
const dt = (s) => (s ? new Date(s).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
// Privacy: show first name + last initial on the leaderboard (e.g. "Holly R.")
// so referrers never see each other's full surnames. The API already abbreviates
// these names server-side; this is a defensive, idempotent ("Holly R." → "Holly R.")
// fallback so the portal renders correctly even before the backend deploy lands.
const abbreviateName = (full) => {
  const cleaned = String(full ?? '').trim().replace(/\s+/g, ' ');
  if (!cleaned) return '';
  const parts = cleaned.split(' ');
  if (parts.length === 1) return parts[0];
  const lastInitial = [...parts[parts.length - 1]][0] || '';
  return lastInitial ? `${parts[0]} ${lastInitial.toUpperCase()}.` : parts[0];
};
// B-REFERRAL-SUCCESS-PAID (2026-06-15, operator directive): a referral only counts
// as "Successful" once the £10 is PAID. A completed-but-unpaid takeover is still
// awaiting payment and can be rejected, so it shows as "Processing" — never as
// successful. (`paid` is the only status the leaderboard / Successful count includes.)
const STATUS_LABEL = {
  paid: 'Successful',
  processing: 'Processing',
  submission_not_complete: 'Submission Not Complete',
  // legacy fallbacks: a completed-but-unpaid referral (older 'awaiting_payment' /
  // raw 'successful') is "Processing" until paid; un-finished ones map to incomplete.
  awaiting_payment: 'Processing', successful: 'Processing',
  in_progress: 'Submission Not Complete', dropped_off: 'Submission Not Complete',
};

export default function PortalPage({ token }) {
  const [state, setState] = useState({ loading: true, error: null });
  const [me, setMe] = useState(null);
  const [stats, setStats] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [board, setBoard] = useState([]);
  const [rank, setRank] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [m, s, r, lb] = await Promise.all([
          getMe(token), getStats(token), getReferrals(token), getLeaderboard(token),
        ]);
        if (cancelled) return;
        setMe(m); setStats(s);
        setReferrals(r?.data || []);
        setBoard(lb?.data || []);
        setRank(lb?.callerRank ?? null);
        setState({ loading: false, error: null });
      } catch (err) {
        if (cancelled) return;
        setState({ loading: false, error: err.status === 401 ? 'invalid' : (err.message || 'Failed to load') });
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  if (state.loading) return <div className="center-msg">Loading your portal…</div>;
  if (state.error === 'invalid') {
    return (
      <div className="center-msg">
        <h1 className="title">Link not valid</h1>
        <p className="lead">This portal link is invalid or has been revoked. Please contact the team for a fresh link.</p>
      </div>
    );
  }
  if (state.error) return <div className="center-msg">{state.error}</div>;

  const copyLink = () => {
    if (me?.referralUrl) {
      navigator.clipboard?.writeText(me.referralUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  return (
    <div className="wrap">
      <div className="brand">VANTAGE · Referral Portal</div>
      <h1 className="title">Welcome back, {me?.name?.split(' ')[0] || 'there'}</h1>
      <p className="lead">{me?.tierName ? `${me.tierName} · ` : ''}Share your link, track your earnings.</p>

      {me?.referralUrl && (
        <div className="card">
          <h2>Your referral link</h2>
          <div className="link-box">
            <code>{me.referralUrl}</code>
            <button className="btn-ghost" onClick={copyLink}>{copied ? 'Copied!' : 'Copy'}</button>
          </div>
        </div>
      )}

      <ShareKit referralUrl={me?.referralUrl} />

      <div className="stat-row">
        <div className="stat"><div className="k">Total earnings</div><div className="v">{gbp(stats?.totalEarningsMinor)}</div></div>
        <div className="stat"><div className="k">Pending</div><div className="v">{gbp(stats?.pendingEarningsMinor)}</div></div>
        <div className="stat"><div className="k">Paid</div><div className="v">{gbp(stats?.paidEarningsMinor)}</div></div>
        <div className="stat"><div className="k">Successful</div><div className="v">{stats?.successful ?? 0}</div></div>
      </div>
      <div className="stat-row">
        <div className="stat"><div className="k">Link clicks</div><div className="v">{stats?.clicks ?? 0}</div></div>
        <div className="stat"><div className="k">Processing</div><div className="v">{stats?.processing ?? 0}</div></div>
        <div className="stat"><div className="k">Click → completion</div><div className="v">{stats?.clickToCompletionPct != null ? `${stats.clickToCompletionPct}%` : '—'}</div></div>
        <div className="stat"><div className="k">Your rank</div><div className="v">{rank ? `#${rank}` : '—'}</div></div>
      </div>

      <div className="card">
        <h2>Your referrals</h2>
        <table className="t">
          <thead><tr><th>Name</th><th>Date</th><th>Status</th><th>Payout</th></tr></thead>
          <tbody>
            {referrals.length === 0 && <tr><td colSpan={4} style={{ color: 'var(--text3)' }}>No referrals yet — share your link to get started.</td></tr>}
            {referrals.map((r) => (
              <tr key={r.id}>
                <td>{r.referredName || 'Unnamed (didn’t start)'}</td>
                <td>{dt(r.createdAt)}</td>
                <td><span className={`pill ${r.status}`}>{STATUS_LABEL[r.status] || r.status}</span></td>
                {/* Only a PAID payout shows an amount. A "Processing" row is deliberately
                    shown as "—" (not "£X pending") so we never imply unconfirmed money is
                    secured — it can still be rejected (B-REFERRAL-SUCCESS-PAID). */}
                <td>{r.payoutStatus === 'paid' ? gbp(r.amountMinor) + ' paid' : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="card">
        <h2>Leaderboard</h2>
        <table className="t">
          <thead><tr><th>#</th><th>Referrer</th><th>Successful</th><th>Earned</th></tr></thead>
          <tbody>
            {board.map((e) => (
              <tr key={e.referrerId} style={e.referrerId === me?.id ? { background: '#FFF4ED' } : undefined}>
                <td>{e.rank}</td>
                <td>{abbreviateName(e.name)}{e.referrerId === me?.id ? ' (you)' : ''}</td>
                <td>{e.successfulCount}</td>
                <td>{gbp(e.earnedMinor)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
